import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { tendersAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { FileText, Download, Copy, ArrowLeft, Zap, RefreshCw, CheckCircle } from "lucide-react";
import toast from "react-hot-toast";

const DOCUMENT_TYPES = [
  { id: "expression_of_interest", label: "Expression of Interest (EOI)", desc: "Formal letter expressing interest in the tender" },
  { id: "cover_letter", label: "Bid Cover Letter", desc: "Professional cover letter for your bid submission" },
  { id: "technical_proposal", label: "Technical Proposal Outline", desc: "Structured technical proposal framework" },
  { id: "company_profile", label: "Company Profile Letter", desc: "Company introduction and capability statement" },
];

export default function BidGenerator() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tender, setTender] = useState(null);
  const [docType, setDocType] = useState("expression_of_interest");
  const [companyName, setCompanyName] = useState("");
  const [contactPerson, setContactPerson] = useState(user?.full_name || "");
  const [companyStrength, setCompanyStrength] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generatedDoc, setGeneratedDoc] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    tendersAPI.get(id).then(res => setTender(res.data)).catch(() => navigate("/tenders"));
  }, [id, navigate]);

  const generateDocument = async () => {
    if (!companyName.trim()) { toast.error("Please enter your company name"); return; }
    setGenerating(true);
    setGeneratedDoc("");
    try {
      const token = localStorage.getItem("access_token");
      const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
      const response = await fetch(`${API}/ai/bid-assistant`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({
          tender_id: parseInt(id),
          doc_type: docType,
          company_name: companyName,
          contact_person: contactPerson,
          company_strength: companyStrength,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 403) throw new Error(data.detail || "Enterprise plan or higher required.");
        throw new Error(data.detail || "Generation failed");
      }
      setGeneratedDoc(data.document || "");
      toast.success("Document generated successfully!");
    } catch (err) {
      toast.error(err.message || "Generation failed. Please try again.");
    } finally { setGenerating(false); }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedDoc);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadAsTxt = () => {
    const blob = new Blob([generatedDoc], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${docType}_${tender?.reference_number?.replace(/\//g, "_")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Document downloaded!");
  };

  if (!tender) return <div style={{ textAlign: "center", padding: "60px", color: "var(--text-muted)" }}>Loading...</div>;

  return (
    <div style={{ maxWidth: "960px" }}>
      <button onClick={() => navigate(`/tenders/${id}`)} style={{ display: "flex", alignItems: "center", gap: "8px", background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "14px", marginBottom: "24px", padding: 0 }}>
        <ArrowLeft size={16} /> Back to Tender
      </button>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
        <div style={{ width: "40px", height: "40px", background: "var(--accent-green-dim)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Zap size={20} color="var(--accent-green)" />
        </div>
        <div>
          <h1 style={{ fontSize: "24px" }}>AI Bid Document Generator</h1>
          <p style={{ color: "var(--text-secondary)", fontSize: "13px" }}>Powered by Claude AI � Professional documents in seconds</p>
        </div>
      </div>
      <div className="card" style={{ marginTop: "20px", marginBottom: "24px", borderLeft: "3px solid var(--accent-green)", background: "linear-gradient(135deg, rgba(0,212,161,0.05), transparent)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <p style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>GENERATING FOR</p>
            <h3 style={{ fontSize: "15px", fontWeight: "600", marginBottom: "4px" }}>{tender.title}</h3>
            <p style={{ fontSize: "13px", color: "var(--text-secondary)" }}>{tender.agency} � Ref: {tender.reference_number}</p>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "var(--accent-green)", fontFamily: "Syne" }}>{tender.contract_value_formatted}</div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{tender.days_remaining} days left</div>
          </div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: "24px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div className="card">
            <h3 style={{ fontSize: "14px", marginBottom: "14px", color: "var(--text-secondary)" }}>DOCUMENT TYPE</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {DOCUMENT_TYPES.map(type => (
                <button key={type.id} onClick={() => setDocType(type.id)} style={{ padding: "12px", borderRadius: "10px", border: "1px solid", cursor: "pointer", textAlign: "left", transition: "all 0.15s", background: docType === type.id ? "var(--accent-green-dim)" : "var(--bg-secondary)", borderColor: docType === type.id ? "var(--accent-green)" : "var(--border)" }}>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: docType === type.id ? "var(--accent-green)" : "var(--text-primary)", marginBottom: "2px" }}>{type.label}</div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>{type.desc}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="card">
            <h3 style={{ fontSize: "14px", marginBottom: "14px", color: "var(--text-secondary)" }}>YOUR DETAILS</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "6px", display: "block" }}>Company Name *</label>
                <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Your Company Ltd" />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "6px", display: "block" }}>Contact Person</label>
                <input value={contactPerson} onChange={e => setContactPerson(e.target.value)} placeholder="Full Name" />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "6px", display: "block" }}>Company Strengths (optional)</label>
                <textarea value={companyStrength} onChange={e => setCompanyStrength(e.target.value)} placeholder="e.g. 15 years experience in oil and gas, ISO 9001 certified..." rows={3} style={{ resize: "vertical" }} />
              </div>
            </div>
          </div>
          <button className="btn btn-primary" onClick={generateDocument} disabled={generating} style={{ width: "100%", justifyContent: "center", padding: "14px", fontSize: "15px" }}>
            {generating ? <><RefreshCw size={16} style={{ animation: "spin 1s linear infinite" }} /> Generating...</> : <><Zap size={16} /> Generate Document</>}
          </button>
        </div>
        <div className="card" style={{ display: "flex", flexDirection: "column", minHeight: "500px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h3 style={{ fontSize: "15px" }}>Generated Document</h3>
            {generatedDoc && (
              <div style={{ display: "flex", gap: "8px" }}>
                <button className="btn btn-ghost" style={{ padding: "6px 12px", fontSize: "12px" }} onClick={copyToClipboard}>
                  {copied ? <CheckCircle size={13} color="var(--accent-green)" /> : <Copy size={13} />}
                  {copied ? "Copied!" : "Copy"}
                </button>
                <button className="btn btn-primary" style={{ padding: "6px 12px", fontSize: "12px" }} onClick={downloadAsTxt}>
                  <Download size={13} /> Download
                </button>
              </div>
            )}
          </div>
          {!generatedDoc && !generating && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", color: "var(--text-muted)" }}>
              <FileText size={48} style={{ marginBottom: "16px", opacity: 0.3 }} />
              <p style={{ fontSize: "14px", textAlign: "center" }}>Fill in your details and click Generate to create a professional bid document using AI</p>
            </div>
          )}
          {generating && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px" }}>
              <div style={{ fontSize: "40px", marginBottom: "16px" }}>??</div>
              <p style={{ color: "var(--accent-green)", fontSize: "14px", fontWeight: "600" }}>Claude AI is writing your document...</p>
              <p style={{ color: "var(--text-muted)", fontSize: "12px", marginTop: "8px" }}>This takes 10-20 seconds</p>
            </div>
          )}
          {generatedDoc && (
            <div style={{ flex: 1, overflow: "auto" }}>
              <pre style={{ fontFamily: "DM Sans, sans-serif", fontSize: "13px", lineHeight: "1.8", color: "var(--text-primary)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{generatedDoc}</pre>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
